/**
 * Reference: https://github.com/Vicimus/games-demo/blob/main/src/app/components/spin-the-wheel/spin-the-wheel.component.ts
 * Modified to fit BF2
 */

import {
    ChangeDetectionStrategy, ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output, signal,
    Signal,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PlayableGame } from '../../interfaces/playable-game';
import { Prize } from '../../interfaces/prize';
import { Segment } from '../../interfaces/segment';
import { computeWheelSegments, normAngle } from '../../utils/utils';
import { GamePrizeType, WinningPrize } from '../../interfaces/game';
import { WheelAudioService } from '../../services/wheel-audio.service';
import { WinOverlayComponent } from '../win-overlay/win-overlay.component';

const SPIN_DURATION_MS = 8000;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-spin-the-wheel',
    standalone: true,
    imports: [CommonModule, WinOverlayComponent],
    templateUrl: './spin-the-wheel.component.html',
    styleUrl: './spin-the-wheel.component.scss',
})
export class SpinTheWheelComponent implements PlayableGame, OnDestroy {
    @Input({ required: true }) public drawWinner!: () => Promise<WinningPrize | null>;

    @Output() public readonly finished = new EventEmitter<void>();

    public hasSpun = signal(false);

    public overlayVisible = signal(false);

    public rotation = 0;

    public segments: Signal<Segment[]> = computed(() => computeWheelSegments(this.prizes));

    @Input() public standalone = false;

    @Input() public winningPrize?: WinningPrize | null;

    public winningPrizeDisplay: { name: string, image: string, type: GamePrizeType } | null = null;

    private readonly _prizes = signal<Prize[]>([]);

    @ViewChild('spinButton') private spinButton!: ElementRef<HTMLButtonElement>;

    public constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly audio: WheelAudioService,
    ) {
    }

    public get prizes(): Prize[] {
        return this._prizes();
    }

    @Input({ required: true }) public set prizes(value: Prize[]) {
        this._prizes.set(value);
    }

    public ngOnDestroy(): void {
        this.audio.destroy();
    }

    public async spin(): Promise<void> {
        this.hasSpun.set(true);
        const winningPrize = await this.drawWinner();
        if (!winningPrize) return;

        const index = this.prizes.findIndex(
            p => p.item.id === winningPrize.item_id && p.type === winningPrize?.type,
        );
        if (index < 0) return;

        const count = this.prizes.length;
        if (!count) return;

        const anglePer = 360 / count;
        const center = normAngle(90 + index * anglePer + anglePer / 2);

        const fullRotations = 5;
        const targetRotation = fullRotations * 360 + (360 - Math.round(center));

        this.rotation += targetRotation;
        this.cdr.markForCheck();
        this.spinButton?.nativeElement.classList.add('clicked');

        this.audio.scheduleTicks(targetRotation, count, SPIN_DURATION_MS);

        setTimeout(() => {
            const segments = this.segments();
            const prizes = this.prizes;
            const s = segments[index];
            const p = prizes[index];
            if (s) {
                s.selected = true;
                this.winningPrizeDisplay = {
                    name: p.item.name,
                    image: p.item.image_url ?? '',
                    type: p.type,
                };
                this.overlayVisible.set(true);
            }
            this.cdr.markForCheck();
        }, SPIN_DURATION_MS);
    }
}
